/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Hidden, Tag, Typography2, breakpoints } from '@coursera/cds-core';
import type { AdminType } from '@coursera/event-pulse-types';
import { track } from '@coursera/event-pulse/sdk';

import GlobalHelpButton from 'bundles/common/components/GlobalHelpButton';
import { PrivateAuthoringPartnerContext } from 'bundles/enterprise-admin-custom-courses/components/PrivateAuthoringPartnerProvider';
import { noPrivateAuthoringCanUseCustomCourses } from 'bundles/enterprise-admin-custom-courses/utils/helpers';
import { getPrivateAuthoringPartnerIdForCourseBuilder } from 'bundles/enterprise-admin-custom-courses/utils/privateAuthoringUtils';
import { ADMIN_HOME_V2_REF } from 'bundles/enterprise-admin-home/constants';
import AdminNavigationDropdown from 'bundles/enterprise-admin-navigation/components/AdminNavigationDropdown';
import CourseraHeader from 'bundles/enterprise-admin-navigation/components/CourseraHeader';
import EnterpriseCoachChatLauncher from 'bundles/enterprise-admin-navigation/components/EnterpriseCoachChatLauncher';
import MenuItem from 'bundles/enterprise-admin-navigation/components/MenuItem';
import NavMenu from 'bundles/enterprise-admin-navigation/components/NavMenu';
import { ENTERPRISE_ADMIN_NAVBAR_HEIGHT_PX } from 'bundles/enterprise-admin-navigation/constants';
import { getMenuLinks } from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';
import withManagedOrganizations from 'bundles/enterprise-admin-navigation/utils/withManagedOrganizations';
import { EnterpriseAdminPermissionsContext } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import { EnterpriseAdminContext } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';
import AdminAssistEpicClient from 'bundles/epic/clients/admin_assist';

import _t from 'i18n!nls/page';

type PropsFromCaller = {
  thirdPartyOrganization: AdminDashboardOrg;
  adminRoleType?: string;
  showCoach: boolean;
  toggleCoach: () => void;
};

type PropsFromOrganizations = {
  thirdPartyOrganizations: AdminDashboardOrg[];
};

type Props = PropsFromCaller & PropsFromOrganizations;

const styles = {
  coachLauncher: css`
    ${breakpoints.up('md')} {
      margin-right: -2px;
    }

    [role='button'],
    button {
      transform: scale(0.75);
      border-radius: var(--cds-border-radius-50);
    }
  `,
  coachLauncherContainer: css`
    display: flex;
    align-items: center;
    ${breakpoints.down('md')} {
      padding: var(--cds-spacing-50) var(--cds-spacing-100);
      border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  newTag: css`
    margin-left: 0;
    ${breakpoints.down('md')} {
      margin-left: var(--cds-spacing-100);
    }
  `,
};

export class AdminHeaderAndNav extends React.Component<Props> {
  private prevPathName?: string;

  static contextTypes = {
    router: PropTypes.object.isRequired,
    enableSkillsPlanningForOrg: PropTypes.bool.isRequired,
    enableNewLearnersPage: PropTypes.bool.isRequired,
  };

  declare context: LegacyContextType<typeof AdminHeaderAndNav.contextTypes>;

  componentDidMount() {
    const { router } = this.context;
    this.trackPageView();
    this.prevPathName = router.location.pathname;
  }

  componentDidUpdate() {
    const { router } = this.context;
    // Track on route changes
    if (router.location.pathname !== this.prevPathName) {
      this.trackPageView();
      this.prevPathName = router.location.pathname;
    }
  }

  handleCoachLauncherClick = () => {
    this.props.toggleCoach();
  };

  /* Tracking page view through 'view_admin_dashboard_page' event as the default 'view_page' event tracks unauthorized views as well.
    Additionally, adminType property is required for the event. */
  trackPageView() {
    const { thirdPartyOrganization, adminRoleType } = this.props;
    track('view_admin_dashboard_page', {
      enterprise: {
        organizationSlug: thirdPartyOrganization.slug,
      },
      enterpriseAdminAccount: {
        adminType: (adminRoleType ? adminRoleType.toLowerCase().replace(/ /g, '_') : undefined) as AdminType,
      },
    });
  }

  renderMenuLinks(canUseCourseBuilder: boolean) {
    const { thirdPartyOrganization } = this.props;
    const { router, enableSkillsPlanningForOrg, enableNewLearnersPage } = this.context;

    const locationSearch = new URLSearchParams(router.location.search || '');
    if (locationSearch.get('ref') === ADMIN_HOME_V2_REF) {
      locationSearch.delete('ref');
    }
    const locationSearchString = locationSearch.size > 0 ? `?${locationSearch.toString()}` : '';

    return getMenuLinks(
      router.params,
      enableSkillsPlanningForOrg,
      canUseCourseBuilder,
      thirdPartyOrganization.id,
      enableNewLearnersPage
    ).map((item) => {
      const { id, href, text, requiredPermissions, phaseOneOverride } = item;

      /* Fix for Programs tab: don't show as active when on contracts route
        since contracts is a child route of programs 
      */
      const isActive =
        id === 'PROGRAMS'
          ? router.isActive(href) && !router.location.pathname.includes('/contracts')
          : router.isActive(href);

      return requiredPermissions ? (
        <EnterprisePermissionsRequired
          domain={requiredPermissions.domain}
          domainId={requiredPermissions.domainId}
          subdomain={requiredPermissions.subdomain}
          verb={requiredPermissions.verb}
          phaseOneOverride={phaseOneOverride}
          key={id}
        >
          <MenuItem
            isActive={isActive}
            text={text}
            tag="link"
            thirdPartyOrganizationId={thirdPartyOrganization.id}
            htmlAttributes={{
              href: `${href}${locationSearchString}`,
              'data-e2e': `Menu_Link~${text}`,
            }}
          />
        </EnterprisePermissionsRequired>
      ) : (
        <MenuItem
          key={id}
          isActive={isActive}
          text={text}
          tag="link"
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          htmlAttributes={{
            href: `${href}${locationSearchString}`,
            'data-e2e': `Menu_Link~${text}`,
          }}
        />
      );
    });
  }

  renderCoachLauncher = () => {
    const hideConversationalAssistant = (() => {
      const excludedOrgs = AdminAssistEpicClient.get('hide_conversational_assistant');
      if (!excludedOrgs) {
        return false; // Show by default if config not loaded
      }
      if (!Array.isArray(excludedOrgs)) {
        console.error('[AdminHeaderAndNav] Invalid hide_conversational_assistant config:', excludedOrgs);
        return false;
      }
      return excludedOrgs.includes(this.props.thirdPartyOrganization.slug);
    })();
    if (hideConversationalAssistant) {
      return null;
    }
    return (
      <div css={styles.coachLauncherContainer}>
        <div css={styles.coachLauncher}>
          <EnterpriseCoachChatLauncher
            onClick={this.handleCoachLauncherClick}
            defaultSelected={this.props.showCoach || false}
            tooltip={_t('Open AI Assistant')}
            size="medium"
          />
        </div>
        <Hidden mdUp>
          <Typography2 variant="bodySecondary" component="span">
            {_t('AI Assistant')}
          </Typography2>
        </Hidden>
        <Tag variant="status" priority="primary" color="highlight1" css={styles.newTag}>
          {_t('New')}
        </Tag>
      </div>
    );
  };

  renderLeftChildren = (canUseCourseBuilder: boolean) => {
    return (
      <NavMenu>
        {this.renderMenuLinks(canUseCourseBuilder)}
        {this.renderCoachLauncher()}
      </NavMenu>
    );
  };

  render() {
    const { thirdPartyOrganization, thirdPartyOrganizations } = this.props;

    const rightChildren = (
      <AdminNavigationDropdown
        isDirectSale={enterpriseAdminUser.isStandardContractUser()}
        isSelfServe={!enterpriseAdminUser.isStandardContractUser()}
        thirdPartyOrganization={thirdPartyOrganization}
        thirdPartyOrganizations={thirdPartyOrganizations}
        renderCoachLauncher={this.renderCoachLauncher}
      />
    );

    return (
      <div data-testid="AdminHeaderAndNav">
        <div data-testid="mobile">
          <Hidden mdUp>
            <CourseraHeader
              navHeight={ENTERPRISE_ADMIN_NAVBAR_HEIGHT_PX}
              leftChildren={null}
              rightChildren={rightChildren}
            />
          </Hidden>
        </div>
        <div data-testid="desktop">
          <Hidden smDown>
            <PrivateAuthoringPartnerContext.Consumer>
              {({ enterpriseAuthoringPartners }) => (
                <EnterpriseAdminContext.Consumer>
                  {({ contracts }) => (
                    <EnterpriseAdminPermissionsContext.Consumer>
                      {({ getIsOrganizationDomainAdmin }) => {
                        const canUseCourseBuilder =
                          noPrivateAuthoringCanUseCustomCourses(
                            getIsOrganizationDomainAdmin(),
                            enterpriseAdminUser.isStandardContractUser(),
                            contracts
                          ) || !!getPrivateAuthoringPartnerIdForCourseBuilder(enterpriseAuthoringPartners);

                        return (
                          <CourseraHeader
                            navHeight={ENTERPRISE_ADMIN_NAVBAR_HEIGHT_PX}
                            leftChildren={this.renderLeftChildren(canUseCourseBuilder)}
                            rightChildren={rightChildren}
                          />
                        );
                      }}
                    </EnterpriseAdminPermissionsContext.Consumer>
                  )}
                </EnterpriseAdminContext.Consumer>
              )}
            </PrivateAuthoringPartnerContext.Consumer>
          </Hidden>
        </div>
        <GlobalHelpButton />
      </div>
    );
  }
}

export default withManagedOrganizations<Props, PropsFromCaller>(AdminHeaderAndNav);
